import React, { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { toCsv } from '../../utils/csv.js';

// ─── VAT reclaim ─────────────────────────────────────────────────────────────
//
// Not a list of what went to Sage. A list of the input VAT that is at risk of
// never being reclaimed, which is a different and more valuable question.
//
// Under-reclaiming is rarely dramatic. It is an invoice logged with £0 VAT
// because the VAT box was missed, a reclaim that cannot be evidenced because
// the supplier's VAT number was never captured, or an invoice that sat in ops
// and never reached the ledger at all. Each of those is worth real money and
// none of them announces itself, so they are listed here in order of cost.
//
// Every figure that is an estimate says so. Telling somebody they are owed
// money that turns out not to exist is worse than saying nothing.

const T = {
  label: 'text-[9px] font-semibold uppercase tracking-wide',
  meta: 'text-[10px]',
  body: 'text-[11px]',
  strong: 'text-[12px] font-semibold',
  page: 'text-base font-extrabold',
};
const btn = 'px-3 py-1.5 rounded-md border text-[11px] font-semibold transition-colors disabled:opacity-50';
const inp = 'border border-gray-200 rounded px-2 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-navy';

const money = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS = {
  possible_underclaim: { label: 'VAT possibly missed', cls: 'bg-red-100 text-red-800 border-red-200' },
  blocked:             { label: 'Figures do not add up', cls: 'bg-red-100 text-red-800 border-red-200' },
  at_risk:             { label: 'Evidence missing',      cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  ok:                  { label: 'Not in Sage yet',       cls: 'bg-blue-100 text-blue-800 border-blue-200' },
};

const TREATMENTS = [
  ['standard',       'Standard rated (20%)'],
  ['reduced',        'Reduced rate (5%)'],
  ['zero',           'Zero rated (0%)'],
  ['exempt',         'Exempt from VAT'],
  ['reverse_charge', 'Reverse charge'],
  ['not_registered', 'Supplier not VAT registered'],
];

// One exception, and the two things a person can do about it: correct the VAT
// number, or record why the invoice is right as it stands. Both close it.
function ExceptionRow({ ex, onResolved }) {
  const [open, setOpen]     = useState(false);
  const [treat, setTreat]   = useState(ex.vat_treatment || '');
  const [vatNo, setVatNo]   = useState('');
  const [note, setNote]     = useState('');
  const [busy, setBusy]     = useState(false);

  const cfg = STATUS[ex.status] || STATUS.ok;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`sage/vat-review/${ex.id}`, {
        vat_treatment: treat || null,
        supplier_vat_number: vatNo.trim() || null,
        note: note.trim() || null,
      });
      onResolved(ex.id);
    } catch (e) { alert(e.message || 'Could not save that.'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <tr className={open ? 'bg-gray-50' : ''}>
        <td className="px-3 py-1.5">
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${cfg.cls}`}>{cfg.label}</span>
        </td>
        <td className={`${T.body} text-gray-800 px-3 py-1.5`}>
          <div className="font-semibold">{ex.supplier || 'Unknown supplier'}</div>
          <div className={`${T.meta} text-gray-400`}>{ex.invoice_number || 'no number'} · {ex.invoice_date ? formatDate(ex.invoice_date) : 'no date'}</div>
        </td>
        <td className={`${T.body} text-right px-3 py-1.5 tabular-nums text-gray-600`}>{money(ex.net)}</td>
        <td className={`${T.body} text-right px-3 py-1.5 tabular-nums ${ex.vat > 0 ? 'text-gray-800' : 'text-red-600 font-semibold'}`}>{money(ex.vat)}</td>
        <td className={`${T.body} text-right px-3 py-1.5 tabular-nums`}>
          {ex.potential_underclaim > 0
            ? <span className="text-red-700 font-semibold">{money(ex.potential_underclaim)}</span>
            : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-3 py-1.5 text-right">
          <button onClick={() => setOpen(o => !o)} className={`${T.meta} text-navy hover:underline`}>
            {open ? 'close' : 'why'}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50">
          <td colSpan={6} className="px-3 pb-3">
            <div className="space-y-1.5">
              {[...(ex.blockers || []), ...(ex.opportunities || []), ...(ex.risks || [])].map((r, i) => (
                <div key={i} className={`${T.meta} text-gray-700`}>• {r}</div>
              ))}
              {!ex.in_sage && (
                <div className={`${T.meta} text-gray-700`}>
                  • Logged in ops but not posted to Sage, so nothing has been reclaimed for it yet.
                </div>
              )}
              {ex.supplier_charges_vat && !(ex.vat > 0) && (
                <div className={`${T.meta} text-red-700`}>
                  • This supplier has charged VAT on other invoices, which makes a £0 here worth checking against the document.
                </div>
              )}

              <div className="pt-2 mt-1 border-t border-gray-200">
                <div className={`${T.label} text-gray-500 mb-1`}>Record what is actually right</div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <select className={inp} value={treat} onChange={e => setTreat(e.target.value)}>
                    <option value="">— VAT treatment —</option>
                    {TREATMENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <input className={inp} placeholder="Supplier VAT no." value={vatNo} onChange={e => setVatNo(e.target.value)} />
                  <input className={`${inp} flex-1 min-w-[10rem]`} placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} />
                  <button onClick={save} disabled={busy} className={`${btn} bg-navy text-white border-navy hover:bg-navy/90`}>
                    {busy ? 'Saving…' : 'Mark reviewed'}
                  </button>
                </div>
                <p className={`${T.meta} text-gray-500 mt-1`}>
                  Marking it reviewed takes it off this list and keeps it off. A VAT number entered here is also saved
                  against the supplier, so the next invoice from them starts with the evidence already in place.
                  To change the figures themselves, edit the invoice in the Accounts Inbox.
                </p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── The whole-year audit ────────────────────────────────────────────────────
//
// "Since 1 December, what has had its VAT reclaimed and what has not." The
// exception list above cannot answer that: it reads what ops captured, and ops
// captured nothing before June. This reads the ledger as well, which is the only
// source that covers the year and the only one that says what was actually
// reclaimed rather than what was recorded.

const LIKELIHOOD = {
  confirmed: { label: 'Confirmed missed', cls: 'bg-red-200 text-red-900 border-red-300' },
  likely:    { label: 'Likely missed',    cls: 'bg-red-100 text-red-800 border-red-200' },
  possible:  { label: 'Possible',         cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  unlikely:  { label: 'Probably correct', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  settled:   { label: 'Settled',          cls: 'bg-green-100 text-green-800 border-green-200' },
};

const DISPOSITIONS = [
  ['not_registered',      'Supplier is not VAT registered'],
  ['reverse_charge_cis',  'CIS domestic reverse charge (construction)'],
  ['reverse_charge_eu',   'Reverse charge (EU acquisition or overseas service)'],
  ['exempt',              'Exempt (insurance, finance, postage, rent)'],
  ['zero_rated',          'Zero rated (food, books, transport, export)'],
  ['outside_scope',       'Outside the scope of VAT (statutory fees, HMRC, wages)'],
  ['overseas_no_uk_vat',  'Overseas supplier, no UK VAT to reclaim'],
  ['query_raised',        'Queried with the supplier, waiting'],
  ['underclaimed',        'Confirmed: VAT should have been charged'],
];

function downloadCsv(filename, header, rows) {
  const csv = toCsv(header, rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// One zero-VAT ledger invoice, and the two ways to close it: an answer that
// covers this supplier for good, or an outcome for this one invoice.
function CandidateRow({ c, onClosed }) {
  const [open, setOpen]   = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote]   = useState('');
  const [busy, setBusy]   = useState(false);
  const cfg = LIKELIHOOD[c.likelihood] || LIKELIHOOD.possible;

  const saveSupplier = async () => {
    if (!reason) return;
    setBusy(true);
    try {
      await api.put('sage/vat-disposition', {
        sage_contact_id: c.contact_id, contact_name: c.supplier, reason, note: note.trim() || null,
      });
      onClosed({ contact_id: c.contact_id });
    } catch (e) { alert(e.message || 'Could not save that.'); }
    finally { setBusy(false); }
  };

  const saveInvoice = async (outcome) => {
    setBusy(true);
    try {
      await api.put(`sage/vat-ledger-review/${c.id}`, { outcome, note: note.trim() || null });
      onClosed({ id: c.id });
    } catch (e) { alert(e.message || 'Could not save that.'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <tr className={open ? 'bg-gray-50' : ''}>
        <td className="px-3 py-1.5">
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${cfg.cls}`}>{cfg.label}</span>
        </td>
        <td className={`${T.body} text-gray-800 px-3 py-1.5`}>
          <div className="font-semibold">{c.supplier || 'Unknown supplier'}</div>
          <div className={`${T.meta} text-gray-400`}>
            {c.reference || 'no reference'} · {c.date ? formatDate(c.date) : 'no date'}
          </div>
        </td>
        <td className={`${T.meta} text-gray-600 px-3 py-1.5`}>{c.supplier_pattern_label || '—'}</td>
        <td className={`${T.body} text-right px-3 py-1.5 tabular-nums text-gray-600`}>{money(c.net)}</td>
        <td className={`${T.body} text-right px-3 py-1.5 tabular-nums`}>
          {c.at_stake > 0
            ? <span className={c.likelihood === 'likely' || c.likelihood === 'confirmed' ? 'text-red-700 font-semibold' : 'text-amber-700'}>{money(c.at_stake)}</span>
            : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-3 py-1.5 text-right">
          <button onClick={() => setOpen(o => !o)} className={`${T.meta} text-navy hover:underline`}>
            {open ? 'close' : 'why'}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="bg-gray-50">
          <td colSpan={6} className="px-3 pb-3">
            <div className="space-y-1.5">
              {(c.reasons || []).map((r, i) => <div key={i} className={`${T.meta} text-gray-700`}>• {r}</div>)}
              {c.profile && (
                <div className={`${T.meta} text-gray-500`}>
                  • Across all time: {c.profile.invoices} invoices, {c.profile.with_vat} with VAT,
                  {' '}an effective rate of {c.profile.effective_rate}%
                  {c.profile.last_vat_date ? `, last VAT charged ${formatDate(c.profile.last_vat_date)}` : ', VAT never charged'}.
                </div>
              )}
              <div className={`${T.meta} text-gray-500`}>
                • The £{Number(c.at_stake || 0).toFixed(2)} is what 20% of the net would come to. It is what the
                {' '}reclaim would be worth if VAT should have been charged, not a figure to put in a return.
              </div>

              <div className="pt-2 mt-1 border-t border-gray-200 grid gap-2 sm:grid-cols-2">
                <div>
                  <div className={`${T.label} text-gray-500 mb-1`}>Answer for this supplier, once</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <select className={`${inp} flex-1 min-w-[12rem]`} value={reason} onChange={e => setReason(e.target.value)}>
                      <option value="">— why their invoices carry no VAT —</option>
                      {DISPOSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button onClick={saveSupplier} disabled={busy || !reason}
                      className={`${btn} bg-navy text-white border-navy hover:bg-navy/90`}>Save</button>
                  </div>
                  <p className={`${T.meta} text-gray-500 mt-1`}>
                    Closes every zero-VAT invoice from this supplier, including the ones still to come.
                  </p>
                </div>
                <div>
                  <div className={`${T.label} text-gray-500 mb-1`}>Or answer for this invoice alone</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button onClick={() => saveInvoice('correct')} disabled={busy}
                      className={`${btn} border-gray-300 text-gray-700 bg-white hover:bg-gray-50`}>Correct as it is</button>
                    <button onClick={() => saveInvoice('underclaimed')} disabled={busy}
                      className={`${btn} border-red-300 text-red-700 bg-white hover:bg-red-50`}>VAT was missed</button>
                    <button onClick={() => saveInvoice('query_raised')} disabled={busy}
                      className={`${btn} border-amber-300 text-amber-800 bg-white hover:bg-amber-50`}>Queried</button>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <input className={`${inp} w-full`} placeholder="Note (kept against whichever you save)"
                    value={note} onChange={e => setNote(e.target.value)} />
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Stat({ label, value, hint, tone = 'text-navy' }) {
  return (
    <div className="bg-white rounded border border-gray-200 px-2.5 py-2">
      <div className={`${T.label} text-gray-500`}>{label}</div>
      <div className={`text-[18px] font-bold tabular-nums ${tone}`}>{value}</div>
      <div className={`${T.meta} text-gray-400`}>{hint}</div>
    </div>
  );
}

function VatAudit() {
  const [data, setData]       = useState(null);
  const [fy, setFy]           = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [band, setBand]       = useState('all');

  const load = useCallback(async (y = fy) => {
    setLoading(true); setError(null);
    try { setData(await api.get('sage/vat-audit', { fy: y })); }
    catch (e) { setData(null); setError(e.message || 'Could not load the audit.'); }
    finally { setLoading(false); }
  }, [fy]);

  useEffect(() => { load(fy); }, [fy]); // eslint-disable-line react-hooks/exhaustive-deps

  const s = data?.summary;
  const all = data?.candidates || [];
  const candidates = all.filter(c => band === 'all'
    ? c.likelihood !== 'settled'
    : c.likelihood === band);

  const exportCsv = () => {
    downloadCsv(
      `vat-not-reclaimed-${data?.period?.start || 'period'}.csv`,
      ['Likelihood', 'Supplier', 'Reference', 'Date', 'Net', 'VAT reclaimed', 'At stake at 20%', 'Supplier pattern', 'Why'],
      all.map(c => [c.likelihood, c.supplier, c.reference, c.date, c.net, 0, c.at_stake,
                    c.supplier_pattern_label, (c.reasons || []).join(' ')]),
    );
  };

  const gaps = (data?.coverage || []).filter(c => !c.covers_period);
  const failed = Object.entries(data?.sources || {}).filter(([, v]) => !v.ok);

  return (
    <div>
      <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
        <p className={`${T.meta} text-gray-500 max-w-2xl`}>
          Every purchase in the ledger for the period, split into what has had VAT reclaimed and what has not,
          with the zero-VAT ones ranked by how likely it is that VAT was missed rather than correctly absent.
        </p>
        <div className="flex items-center gap-1.5">
          <select className={inp} value={fy} onChange={e => setFy(Number(e.target.value))}>
            <option value={0}>This financial year</option>
            <option value={1}>Last financial year</option>
            <option value={2}>Two years ago</option>
          </select>
          <button onClick={() => load(fy)} disabled={loading} className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button onClick={exportCsv} disabled={!all.length} className={`${btn} border-gray-300 text-gray-700 bg-white hover:bg-gray-50`}>
            Export
          </button>
        </div>
      </div>

      {data?.period && (
        <p className={`${T.meta} text-gray-500 mb-2`}>
          {data.period.label}: {formatDate(data.period.start)} to {formatDate(data.period.end)}.
        </p>
      )}

      {error && <div className={`mb-3 rounded-lg border border-red-300 bg-red-50 p-3 ${T.body} text-red-900`}>{error}</div>}

      {/* A source that failed to read must never come back as a clean report.
          "No VAT at risk" is the most expensive wrong answer this screen can give. */}
      {failed.map(([name, v]) => (
        <div key={name} className={`mb-2 rounded-lg border border-red-300 bg-red-50 p-3 ${T.body} text-red-900`}>
          <strong>{name}</strong> could not be read, so nothing from it is in the figures below: {v.error}
        </div>
      ))}

      {/* The period asks for more than some sources can show. Saying so is the
          whole point: a silent gap reads as "nothing found there". */}
      {!!gaps.length && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className={`${T.strong} text-amber-900`}>Not every source covers this whole period</div>
          {gaps.map(g => <p key={g.source} className={`${T.meta} text-amber-800 mt-0.5`}>• {g.label}: {g.note}</p>)}
          <p className={`${T.meta} text-amber-800 mt-1`}>
            The Sage ledger does cover it, and it is what the reclaimed and not-reclaimed figures are built from.
            The ops and card figures below are only about the part of the year they can see.
          </p>
        </div>
      )}

      {s && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            <Stat label="VAT reclaimed" value={money(s.vat_reclaimed)} tone="text-green-700"
              hint={`${s.ledger_invoices} invoices in the ledger`} />
            <Stat label="Purchases with no VAT" value={money(s.zero_vat_net)} tone="text-navy"
              hint={`${s.zero_vat_invoices} invoices, net`} />
            <Stat label="Likely missed" value={money(s.at_stake_likely)} tone="text-red-700"
              hint="estimate, at 20%" />
            <Stat label="Possibly missed" value={money(s.at_stake_possible)} tone="text-amber-700"
              hint="estimate, needs a look" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <Stat label="In ops, never posted" value={money(s.ops_unposted_vat)} tone="text-blue-700"
              hint={`${s.ops_unposted_invoices} invoices — VAT recorded, claimed nowhere`} />
            <Stat label="Card VAT not established" value={money(s.card.vat_if_all_standard)} tone="text-amber-700"
              hint={`${s.card.undetermined} of ${s.card.transactions} charges, upper bound`} />
            <Stat label="Card spend with a receipt but no VAT" value={money(s.card.undetermined_with_receipt_gross)}
              tone="text-red-700" hint={`${s.card.undetermined_with_receipt} charges — the evidence is already held`} />
          </div>
        </>
      )}

      {s?.ops_unposted_vat > 0 && (
        <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className={`${T.strong} text-blue-900`}>
            {money(s.ops_unposted_vat)} of VAT is on invoices logged in ops that never reached Sage
          </div>
          <p className={`${T.meta} text-blue-800 mt-1`}>
            This one is not an estimate. The VAT is on the invoice, the invoice is in ops, and it has not been
            posted, so it has not been reclaimed. It is the fastest money on this page: the Sage screen posts them.
          </p>
        </div>
      )}

      {s?.card?.undetermined_with_receipt > 0 && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className={`${T.strong} text-amber-900`}>
            {s.card.undetermined_with_receipt} card charges have a receipt but no VAT read off it
          </div>
          <p className={`${T.meta} text-amber-800 mt-1`}>
            {money(s.card.undetermined_with_receipt_gross)} of spend where the evidence is already held and the
            split has simply not been done. Up to {money(s.card.undetermined_with_receipt_gross / 6)} if all of it
            were standard rated, which it will not be. Card Spend is where these get coded.
          </p>
        </div>
      )}

      <div className="flex gap-1 mb-2 flex-wrap items-center">
        {[
          ['all', `Outstanding (${all.filter(c => c.likelihood !== 'settled').length})`],
          ['confirmed', 'Confirmed'],
          ['likely', `Likely (${all.filter(c => c.likelihood === 'likely').length})`],
          ['possible', `Possible (${all.filter(c => c.likelihood === 'possible').length})`],
          ['unlikely', `Probably correct (${all.filter(c => c.likelihood === 'unlikely').length})`],
          ['settled', `Settled (${all.filter(c => c.likelihood === 'settled').length})`],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setBand(k)}
            className={`px-2.5 py-1 rounded text-[10px] font-semibold border ${
              band === k ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[['Likelihood', 'left'], ['Invoice', 'left'], ['What this supplier normally does', 'left'],
                  ['Net', 'right'], ['At stake', 'right'], ['', 'right']].map(([h, a], i) => (
                  <th key={i} className={`${T.label} text-gray-500 px-3 py-2 text-${a}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {candidates.map(c => (
                <CandidateRow key={c.id} c={c}
                  onClosed={({ id, contact_id }) => setData(d => ({
                    ...d,
                    candidates: (d.candidates || []).filter(x => (id ? x.id !== id : x.contact_id !== contact_id)),
                  }))} />
              ))}
              {!candidates.length && !loading && (
                <tr><td colSpan={6} className={`${T.body} text-gray-500 px-3 py-6 text-center`}>
                  {data ? 'Nothing in this band.' : 'No data.'}
                </td></tr>
              )}
              {loading && <tr><td colSpan={6} className={`${T.body} text-gray-400 px-3 py-6 text-center`}>Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {!!(data?.unposted || []).length && (
        <>
          <h3 className={`${T.strong} text-navy mb-1`}>VAT recorded in ops but never posted to Sage</h3>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>{['Invoice', 'Net', 'VAT', 'Why not posted'].map((h, i) => (
                    <th key={i} className={`${T.label} text-gray-500 px-3 py-2 ${i ? 'text-right' : 'text-left'} ${i === 3 ? '!text-left' : ''}`}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.unposted.map(u => (
                    <tr key={u.id}>
                      <td className={`${T.body} px-3 py-1.5 text-gray-800`}>
                        <div className="font-semibold">{u.supplier || 'Unknown supplier'}</div>
                        <div className={`${T.meta} text-gray-400`}>{u.reference || 'no number'} · {u.date ? formatDate(u.date) : 'no date'}</div>
                      </td>
                      <td className={`${T.body} px-3 py-1.5 text-right tabular-nums text-gray-600`}>{money(u.net)}</td>
                      <td className={`${T.body} px-3 py-1.5 text-right tabular-nums text-blue-700 font-semibold`}>{money(u.vat)}</td>
                      <td className={`${T.meta} px-3 py-1.5 text-gray-600`}>
                        {u.ready
                          ? <span className="text-green-700 font-semibold">Ready to post — nothing is stopping it</span>
                          : (u.blockers || []).join(' ') || 'Not checked yet'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!!(data?.card_merchants || []).length && (
        <>
          <h3 className={`${T.strong} text-navy mb-1`}>Card spend with no VAT established, by merchant</h3>
          <p className={`${T.meta} text-gray-500 mb-1`}>
            A card charge arrives as one gross figure. Until somebody reads the receipt, the VAT inside it is not a
            number anywhere, so this is not "£0 VAT was recorded" — it is "no VAT has been worked out yet".
          </p>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>{[['Merchant', 'left'], ['Charges', 'right'], ['With a receipt', 'right'],
                        ['Gross', 'right'], ['VAT if standard rated', 'right']].map(([h, a], i) => (
                    <th key={i} className={`${T.label} text-gray-500 px-3 py-2 text-${a}`}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.card_merchants.map(m => (
                    <tr key={m.merchant}>
                      <td className={`${T.body} px-3 py-1.5 text-gray-800`}>{m.merchant}</td>
                      <td className={`${T.body} px-3 py-1.5 text-right tabular-nums text-gray-600`}>{m.transactions}</td>
                      <td className={`${T.body} px-3 py-1.5 text-right tabular-nums ${m.with_receipt === m.transactions ? 'text-green-700' : 'text-gray-600'}`}>
                        {m.with_receipt}
                      </td>
                      <td className={`${T.body} px-3 py-1.5 text-right tabular-nums text-gray-600`}>{money(m.gross)}</td>
                      <td className={`${T.body} px-3 py-1.5 text-right tabular-nums text-amber-700`}>{money(m.vat_if_standard)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// The original exception list. Still the right screen for the invoices ops
// itself holds, where the fix is a correction on the invoice rather than a
// question for the supplier.
function OpsExceptions() {
  const [data, setData]     = useState(null);
  const [months, setMonths] = useState(12);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const [error, setError] = useState(null);

  const load = useCallback(async (m = months) => {
    setLoading(true); setError(null);
    try { setData(await api.get('sage/vat-exceptions', { months: m })); }
    catch (e) { setData(null); setError(e.message || 'Could not load the report.'); }
    finally { setLoading(false); }
  }, [months]);

  useEffect(() => { load(months); }, [months]); // eslint-disable-line react-hooks/exhaustive-deps

  const s = data?.summary;
  const exceptions = (data?.exceptions || []).filter(e => filter === 'all' || e.status === filter);

  return (
    <div className="fade-in">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className={`${T.page} text-navy`}>VAT Reclaim</h2>
          <p className={`${T.meta} text-gray-500 mt-0.5`}>
            Input VAT that is at risk of not being reclaimed: missed on the invoice, missing its evidence, or never posted.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <select className={inp} value={months} onChange={e => setMonths(Number(e.target.value))}>
            {[3, 6, 12, 24, 36].map(m => <option key={m} value={m}>Last {m} months</option>)}
          </select>
          <button onClick={() => load(months)} disabled={loading} className={`${btn} border-navy text-navy bg-white hover:bg-navy/5`}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className={`mb-3 rounded-lg border border-red-300 bg-red-50 p-3 ${T.body} text-red-900`}>{error}</div>
      )}

      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {[
            { label: 'VAT recorded', value: money(s.vat_recorded), tone: 'text-navy',
              hint: `${s.invoices} invoices` },
            { label: 'Possibly missed', value: money(s.estimated_underclaim), tone: 'text-red-700',
              hint: 'estimate, at 20%' },
            { label: 'Evidence at risk', value: money(s.vat_at_risk), tone: 'text-amber-700',
              hint: 'claimed without the paperwork' },
            { label: 'Not yet in Sage', value: money(s.vat_not_yet_in_sage), tone: 'text-blue-700',
              hint: 'logged but not posted' },
          ].map(c => (
            <div key={c.label} className="bg-white rounded border border-gray-200 px-2.5 py-2">
              <div className={`${T.label} text-gray-500`}>{c.label}</div>
              <div className={`text-[18px] font-bold tabular-nums ${c.tone}`}>{c.value}</div>
              <div className={`${T.meta} text-gray-400`}>{c.hint}</div>
            </div>
          ))}
        </div>
      )}

      {s?.estimated_underclaim > 0 && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3">
          <div className={`${T.strong} text-red-900`}>
            Around {money(s.estimated_underclaim)} of VAT may be going unclaimed
          </div>
          <p className={`${T.meta} text-red-800 mt-1`}>
            That is an estimate, not a figure to put in a return. It assumes every invoice below with no VAT recorded
            actually carried standard-rate VAT, which will not all be true: some will genuinely be zero-rated, exempt
            or from suppliers who are not registered. Working down the list turns the estimate into a real number,
            and every one you mark reviewed stops it being asked again.
          </p>
        </div>
      )}

      <div className="flex gap-1 mb-2 flex-wrap">
        {[
          ['all', `All (${(data?.exceptions || []).length})`],
          ['possible_underclaim', 'VAT possibly missed'],
          ['at_risk', 'Evidence missing'],
          ['blocked', 'Figures wrong'],
          ['ok', 'Not in Sage yet'],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-2.5 py-1 rounded text-[10px] font-semibold border ${
              filter === k ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[['Status', 'left'], ['Invoice', 'left'], ['Net', 'right'], ['VAT', 'right'], ['Possibly missed', 'right'], ['', 'right']].map(([h, a], i) => (
                  <th key={i} className={`${T.label} text-gray-500 px-3 py-2 text-${a}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {exceptions.map(ex => (
                <ExceptionRow key={ex.id} ex={ex}
                  onResolved={id => setData(d => ({
                    ...d,
                    exceptions: (d.exceptions || []).filter(e => e.id !== id),
                  }))} />
              ))}
              {!exceptions.length && !loading && (
                <tr><td colSpan={6} className={`${T.body} text-gray-500 px-3 py-6 text-center`}>
                  {data ? 'Nothing outstanding here. Every invoice in this period has its VAT established and evidenced.' : 'No data.'}
                </td></tr>
              )}
              {loading && (
                <tr><td colSpan={6} className={`${T.body} text-gray-400 px-3 py-6 text-center`}>Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className={`${T.meta} text-gray-400 mt-2`}>
        Reviewed invoices are hidden. The figures come from the invoices logged in ops, so anything never logged
        at all cannot appear here — the Accounts Inbox is where that gets caught.
      </p>
    </div>
  );
}

// Two questions, and they are genuinely different.
//
// The audit asks "over this financial year, what has been reclaimed and what has
// not" — a period question, answered mostly from the ledger, where the outcome is
// usually a conversation with a supplier.
//
// The exception list asks "of the invoices ops is holding right now, which are
// wrong or unevidenced" — a queue, answered from ops, where the outcome is a
// correction. Folding them together made one screen that answered neither
// properly, because the first needs a year and the second needs today.
export default function VatReclaimPage() {
  const [tab, setTab] = useState('audit');

  return (
    <div className="fade-in">
      <div className="mb-3">
        <h2 className={`${T.page} text-navy`}>VAT Reclaim</h2>
        <p className={`${T.meta} text-gray-500 mt-0.5`}>
          Input VAT that is at risk of not being reclaimed: missed on the invoice, missing its evidence, or never posted.
        </p>
      </div>

      <div className="flex gap-1 mb-3 border-b border-gray-200">
        {[
          ['audit', 'Financial year audit'],
          ['exceptions', 'Invoices in ops'],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-1.5 text-[11px] font-semibold border-b-2 -mb-px transition-colors ${
              tab === k ? 'border-navy text-navy' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'audit' ? <VatAudit /> : <OpsExceptions />}
    </div>
  );
}
